import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, MapPin } from "lucide-react";
import { IMAGES, WA_DEFAULT } from "./data";
import { scrollToId } from "@/lib/lenis";
import { WhatsAppIcon } from "./WhatsAppIcon";

const EASE = [0.22, 1, 0.36, 1];

const MaskedLine = ({ children, delay, className = "" }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className={`block ${className}`}
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.18]);
  const fade = useTransform(scrollYProgress, [0, 0.85], [1, 0]);

  return (
    <section
      id="home"
      ref={ref}
      className="grain relative flex min-h-[100svh] items-end overflow-hidden"
    >
      <motion.div className="absolute inset-0" style={{ y: bgY, scale: bgScale }}>
        <img
          src={IMAGES.hero}
          alt="Commercial gym equipment showroom — strength and cardio machines"
          className="h-full w-full object-cover"
          loading="eager"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/55 to-ink/35" />
      <div
        className="absolute -left-40 top-1/3 h-[480px] w-[480px] rounded-full bg-edge/25 blur-[140px]"
        aria-hidden="true"
      />

      <motion.div
        style={{ opacity: fade }}
        className="relative z-10 mx-auto w-full max-w-7xl px-5 pb-24 pt-40 lg:px-8"
      >
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.15 }}
          className="mb-6 flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.3em] text-ash"
        >
          <span className="inline-block h-2 w-2 bg-edge" aria-hidden="true" />
          Lift Edge — Hercules Fitness Store · Siliguri
        </motion.p>

        <h1
          data-testid="hero-heading"
          className="font-display text-[17vw] font-black uppercase leading-[0.85] tracking-tight text-white sm:text-8xl lg:text-[7.5rem]"
        >
          <MaskedLine delay={0.3}>Build Stronger.</MaskedLine>
          <MaskedLine delay={0.45} className="text-silver-gradient">
            Train Better.
          </MaskedLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.75 }}
          className="mt-7 max-w-xl text-base leading-relaxed text-ash sm:text-lg"
        >
          Tell us your space and your budget — we&rsquo;ll plan, supply and set
          up your complete gym around it. Premium Hercules Fitness equipment
          for gyms, studios, hotels, institutions and homes.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.9 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <a
            href={WA_DEFAULT}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-cta-whatsapp"
            className="clip-corner-sm group flex items-center gap-3 bg-edge px-8 py-4 font-display text-lg font-bold uppercase tracking-wider text-white transition-colors duration-200 hover:bg-edgehot"
          >
            <WhatsAppIcon className="h-5 w-5" />
            Build My Gym Within My Budget
          </a>
          <button
            onClick={() => scrollToId("#products")}
            data-testid="hero-cta-catalogue"
            className="clip-corner-sm group flex items-center gap-3 border border-white/25 bg-white/5 px-8 py-4 font-display text-lg font-bold uppercase tracking-wider text-white backdrop-blur-sm transition-colors duration-200 hover:border-white/60"
          >
            Explore Products
            <ArrowRight
              size={18}
              className="transition-transform duration-200 group-hover:translate-x-1"
            />
          </button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.15 }}
          className="mt-14 flex flex-wrap gap-x-10 gap-y-4 border-t border-white/10 pt-6"
          data-testid="hero-stats-badge"
        >
          {[
            "Gym Setups Planned Around Your Budget",
            "Exclusive Hercules Fitness Showroom",
            "Delivery — North Bengal · Sikkim · Bihar · Nepal · Bhutan",
            "Sales · Setup · After-Sales Service",
          ].map((s) => (
            <span
              key={s}
              className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.22em] text-ash"
            >
              <MapPin size={12} className="text-edge" />
              {s}
            </span>
          ))}
        </motion.div>
      </motion.div>
    </section>
  );
}
